import React, { useEffect, useMemo, useState, useImperativeHandle, useRef, forwardRef } from 'react';
import { Input } from 'antd';
import IconCard from '@web-common/components/UI/IconCard';
import { getIn18Text } from 'api';
import classnames from 'classnames';
import styles from './KeySwiftInput.module.scss';
import { formatHotKey } from '@web-mail/util';
const specialKey = ['metaKey', 'ctrlKey', 'shiftKey', 'altKey'];
const globalShortcutKey = ['Command', 'Ctrl', 'Shift', 'Alt'];
const specialKeyMap = {
    BracketLeft: '[',
    BracketRight: ']',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Equal: '=',
    Minus: '-',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
};
const KeySwiftInput = forwardRef((props, ref) => {
    const { value, onChange, valid, placeholder = getIn18Text('QINGSHURUKUAIJIEJ'), className, style, _data_test_id, onValidError, onValidSuccess, clear = false, onBlur, } = props;
    const [editCaptureInputVal, setEditCaptureInputVal] = useState(value);
    const [conflict, setConflict] = useState(false);
    const InputRef = useRef(null);
    const blurLock = useRef(false);
    useImperativeHandle(ref, () => InputRef.current);
    const getPressKey = (e) => {
        var _a;
        let key = (_a = e === null || e === void 0 ? void 0 : e.nativeEvent) === null || _a === void 0 ? void 0 : _a.code;
        let skip = false;
        if (key) {
            if (specialKeyMap[key]) {
                key = specialKeyMap[key];
                skip = true;
            }
            else {
                key = key.replace('Key', '');
                key = key.replace('Digit', '');
            }
            const registerKeys = [];
            specialKey.forEach((item, index) => {
                if (e[item]) {
                    registerKeys.push(globalShortcutKey[index]);
                }
            });
            if (/^\w$/.test(key) || /^\d$/.test(key) || key === 'Enter' || skip) {
                registerKeys.push(key.toUpperCase());
                return registerKeys;
            }
        }
        return [];
    };
    const handleKeyChange = (keys) => {
        onChange && onChange(keys);
    };
    const handleKeyDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.keyCode === 8) {
            setEditCaptureInputVal([]);
            handleKeyChange([]);
            return;
        }
        const validKeys = getPressKey(e);
        setEditCaptureInputVal(validKeys);
        handleKeyChange(validKeys);
    };
    const handleOnBlur = () => {
        setTimeout(() => {
            if (blurLock.current) {
                blurLock.current = false;
            }
            else {
                onBlur && onBlur();
            }
        }, 100);
    };
    const hotKeyStr = useMemo(() => {
        return formatHotKey(editCaptureInputVal);
    }, [editCaptureInputVal]);
    useEffect(() => {
        if (value != editCaptureInputVal) {
            setEditCaptureInputVal(value);
        }
    }, [value]);
    useEffect(() => {
        if (valid) {
            const res = valid(editCaptureInputVal || []);
            if (typeof res != 'boolean' && res.then != null) {
                res.then(() => {
                    setConflict(false);
                    onValidSuccess && onValidSuccess(editCaptureInputVal || []);
                }, () => {
                    setConflict(true);
                    onValidError && onValidError(editCaptureInputVal || []);
                });
            }
            else {
                if (res === false) {
                    setConflict(true);
                    onValidError && onValidError(editCaptureInputVal || []);
                }
                else {
                    setConflict(false);
                    onValidSuccess && onValidSuccess(editCaptureInputVal || []);
                }
            }
        }
    }, [editCaptureInputVal]);
    return (React.createElement(Input, { ref: InputRef, "data-test-id": _data_test_id, onKeyDown: handleKeyDown, value: hotKeyStr, placeholder: placeholder, style: style, className: classnames(className, {
            [styles.error]: conflict,
        }), onBlur: handleOnBlur, suffix: clear ? (React.createElement("span", { style: { display: 'flex', alignItems: 'center' }, onClick: e => {
                blurLock.current = true;
                setEditCaptureInputVal([]);
                handleKeyChange([]);
            } },
            React.createElement(IconCard, { type: "closeCircle" }))) : null }));
});
export default KeySwiftInput;
